import * as React from 'react';
import { createPortal } from 'react-dom';
import { AddAttachmentPayload } from '@certhon/models';
import { useAttachmentUrl } from '@/features/attachments/useAttachmentUrl';
import { resolveUrl } from '@/lib/api-utils';
import { Button } from '@/components/ui/button';
import { PdfViewerHeadless } from '@/components/ui/PdfViewerHeadless';
import { cn } from '@/lib/utils';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Generic item the Lightbox can display. Caller controls how the URL is
 * constructed (attachments endpoint, /files/:id, signed URL, …).
 */
export interface LightboxItem {
  /** Stable identifier; used to address the item via Lightbox.open(key). */
  key: string;
  /** URL to render (image src / video src / PDF source). */
  url: string;
  /** Optional lower-resolution preview for quick first paint. */
  previewUrl?: string;
  /** MIME type used to pick the renderer (image, video, pdf, …). */
  contentType: string;
  /** Optional filename for download / fallback display. */
  filename?: string;
}

export interface LightboxProps {
  /**
   * Items to display. Either pass legacy attachment payloads (kept for
   * back-compat with existing callers) or generic items.
   */
  attachments?: AddAttachmentPayload[];
  items?: LightboxItem[];
  className?: string;
}

export interface LightboxRef {
  /** Open by stable key. For attachment mode the key is the checksum. */
  open: (key: string) => void;
  close: () => void;
  isOpen: boolean;
}

export const Lightbox = React.forwardRef<LightboxRef, LightboxProps>(
  ({ attachments, items, className }, ref) => {
    const allItems = React.useMemo<LightboxItem[]>(() => {
      if (items) return items;
      if (attachments) return attachments.map(attachmentToItem);
      return [];
    }, [items, attachments]);

    const [isOpen, setIsOpen] = React.useState(false);
    const [currentIndex, setCurrentIndex] = React.useState(0);

    const currentItem = allItems[currentIndex];

    React.useImperativeHandle(ref, () => ({
      open: (key: string) => {
        const index = allItems.findIndex((it) => it.key === key);
        if (index >= 0) {
          setCurrentIndex(index);
          setIsOpen(true);
        }
      },
      close: () => setIsOpen(false),
      isOpen,
    }));

    const handleClose = React.useCallback(() => {
      setIsOpen(false);
    }, []);

    const handleNext = React.useCallback(() => {
      setCurrentIndex((prev) => (prev + 1) % allItems.length);
    }, [allItems.length]);

    const handlePrevious = React.useCallback(() => {
      setCurrentIndex((prev) => (prev - 1 + allItems.length) % allItems.length);
    }, [allItems.length]);

    React.useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        switch (event.key) {
          case 'Escape':
            handleClose();
            break;
          case 'ArrowRight':
            handleNext();
            break;
          case 'ArrowLeft':
            handlePrevious();
            break;
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handleClose, handleNext, handlePrevious]);

    if (!isOpen || !currentItem) return null;

    const lightboxContent = (
      <div
        className={cn(
          'fixed inset-0 z-50 flex items-center justify-center bg-black/90',
          className,
        )}
      >
        {/* Close button.
            Solid white square with a black X. mix-blend-mode:difference
            makes the white pixels render as the negative of whatever is
            behind them (255 - color); the black X passes through
            unchanged (0 difference). backdrop-filter:contrast(2)
            stretches the backdrop pixels toward the extremes BEFORE the
            difference blend runs, so mid-tone backdrops produce a
            darker inverted button and the cutout X gains contrast
            against the rounded background it sits on. */}
        <button
          type="button"
          aria-label="Close"
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 flex size-10 items-center justify-center rounded-md
            bg-white text-black mix-blend-difference"
          style={{
            // grayscale + extreme contrast crushes the backdrop to near
            // pure black/white before the difference blend inverts it,
            // giving the button maximum readability on any backdrop.
            backdropFilter: 'blur(4px) grayscale(1) contrast(8)',
            WebkitBackdropFilter: 'blur(4px) grayscale(1) contrast(8)',
          }}
        >
          <X className="h-6 w-6" />
        </button>

        {/* Navigation buttons */}
        {allItems.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-1/2 left-4 z-10 h-32 w-8 -translate-y-1/2 bg-black/30 text-white
                hover:bg-black/50 hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                handlePrevious();
              }}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="absolute top-1/2 right-4 z-10 h-32 w-8 -translate-y-1/2 bg-black/30 text-white
                hover:bg-black/50 hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          </>
        )}

        {/* Counter */}
        {allItems.length > 1 && (
          <div
            className="absolute top-4 left-1/2 z-10 -translate-x-1/2 rounded bg-black/50 px-3 py-1
              text-sm text-white"
          >
            {currentIndex + 1} of {allItems.length}
          </div>
        )}

        {/* Content. PDFs and images get the whole viewport (the PDF viewer
            adds page margins so the scrollbar sits flush against the screen
            edge; a zoomed image pans across all of it). Other media types
            are constrained to 90vw / 90vh. */}
        {isPdfItem(currentItem) || isImageItem(currentItem) ? (
          <div
            className="absolute inset-0 flex items-stretch justify-stretch"
            onClick={handleClose}
          >
            <div className="h-full w-full" onClick={(e) => e.stopPropagation()}>
              <LightboxContent
                key={currentItem.key}
                item={currentItem}
                onClose={handleClose}
              />
            </div>
          </div>
        ) : (
          <div
            className="flex h-[90vh] w-[90vw] items-center justify-center"
            onClick={handleClose}
          >
            <div className="h-full w-full" onClick={(e) => e.stopPropagation()}>
              <LightboxContent
                key={currentItem.key}
                item={currentItem}
                onClose={handleClose}
              />
            </div>
          </div>
        )}
      </div>
    );

    return createPortal(lightboxContent, document.body);
  },
);

Lightbox.displayName = 'Lightbox';

function isPdfContentType(contentType: string): boolean {
  return contentType === 'application/pdf' || /\bpdf\b/i.test(contentType);
}

/** Whether the Lightbox can show this file (image, video or PDF). */
export function canViewInLightbox(contentType: string): boolean {
  return (
    contentType.startsWith('image/') ||
    contentType.startsWith('video/') ||
    isPdfContentType(contentType)
  );
}

function isPdfItem(item: LightboxItem): boolean {
  return isPdfContentType(item.contentType);
}

function isImageItem(item: LightboxItem): boolean {
  return item.contentType.startsWith('image/');
}

function attachmentToItem(att: AddAttachmentPayload): LightboxItem {
  return {
    key: att.checksum,
    url: resolveUrl(`/attachments/${att.checksum}`),
    contentType: att.contentType as string,
    filename: att.filename,
  };
}

interface LightboxContentProps {
  item: LightboxItem;
  onClose: () => void;
}

function LightboxContent({ item, onClose }: LightboxContentProps) {
  const isImage = item.contentType.startsWith('image/');
  const isVideo = item.contentType.startsWith('video/');
  const isPdf =
    item.contentType === 'application/pdf' || /\bpdf\b/i.test(item.contentType);

  if (isImage) {
    return <ImageContent item={item} onClose={onClose} />;
  }
  if (isVideo) {
    return (
      <video
        src={item.url}
        controls
        autoPlay
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onLoadStart={(e) => {
          const video = e.currentTarget;
          video.muted = true;
          video.play().catch(() => {});
        }}
      >
        Your browser does not support the video tag.
      </video>
    );
  }
  if (isPdf) {
    return <PdfViewerHeadless url={item.url} />;
  }

  return (
    <div className="p-8 text-center text-white">
      <div className="mb-2 text-lg">{item.filename || 'Unknown file'}</div>
      <div className="text-sm text-gray-300">
        File type not supported for preview
      </div>
      <a
        href={item.url}
        target="_blank"
        rel="noreferrer"
        className="mt-4 inline-block text-sm text-blue-300 underline"
      >
        Open in new tab
      </a>
    </div>
  );
}

/** Pointer travel (px) beyond which a press counts as a pan, not a click. */
const DRAG_THRESHOLD = 4;

/**
 * Fitted image; a click toggles 1:1 (actual pixels) around the clicked
 * point, where it can be panned by dragging or scrolling. Only images
 * larger than the fitted size zoom. A click beside the image zooms back
 * out, or closes the lightbox when not zoomed.
 */
function ImageContent({
  item,
  onClose,
}: {
  item: LightboxItem;
  onClose: () => void;
}) {
  const [fullLoaded, setFullLoaded] = React.useState(false);
  // Use the legacy attachment-url hook only when the caller didn't already
  // supply a preview URL. Avoids changing behaviour for attachment-mode
  // callers (which still get the smaller default-profile image first).
  const legacyDefaultUrl = useLegacyAttachmentPreview(item);
  const previewSrc = item.previewUrl || legacyDefaultUrl;

  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const imgRef = React.useRef<HTMLImageElement>(null);
  const [zoomable, setZoomable] = React.useState(false);
  // Clicked point as a fraction of the image + its offset in the scroller,
  // so the same pixel stays under the pointer after zooming in.
  const [zoomAt, setZoomAt] = React.useState<{
    fx: number;
    fy: number;
    x: number;
    y: number;
  } | null>(null);
  const drag = React.useRef<{
    x: number;
    y: number;
    left: number;
    top: number;
    moved: boolean;
  } | null>(null);

  const measure = React.useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return;
    setZoomable(
      img.naturalWidth > img.clientWidth + 1 ||
        img.naturalHeight > img.clientHeight + 1,
    );
  }, []);

  React.useEffect(() => {
    if (zoomAt) return;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [zoomAt, measure]);

  React.useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const img = imgRef.current;
    if (!zoomAt || !scroller || !img) return;
    scroller.scrollLeft =
      img.offsetLeft + zoomAt.fx * img.naturalWidth - zoomAt.x;
    scroller.scrollTop =
      img.offsetTop + zoomAt.fy * img.naturalHeight - zoomAt.y;
  }, [zoomAt]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current;
    if (!scroller || e.button !== 0) return;
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      left: scroller.scrollLeft,
      top: scroller.scrollTop,
      moved: false,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const scroller = scrollerRef.current;
    if (!d || !scroller) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    d.moved = true;
    // Touch pans natively through the scroller; mouse pans by dragging.
    if (zoomAt && e.pointerType === 'mouse') {
      scroller.scrollLeft = d.left - dx;
      scroller.scrollTop = d.top - dy;
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const moved = drag.current?.moved;
    drag.current = null;
    if (moved) return;
    if (zoomAt) {
      setZoomAt(null);
      return;
    }
    const scroller = scrollerRef.current;
    const img = imgRef.current;
    if (!(e.target instanceof HTMLImageElement)) {
      onClose();
      return;
    }
    if (!zoomable || !scroller || !img) return;
    const imgRect = img.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    setZoomAt({
      fx: (e.clientX - imgRect.left) / imgRect.width,
      fy: (e.clientY - imgRect.top) / imgRect.height,
      x: e.clientX - scrollerRect.left,
      y: e.clientY - scrollerRect.top,
    });
  };

  return (
    <div
      ref={scrollerRef}
      className={cn(
        'h-full w-full overscroll-contain',
        zoomAt ? 'overflow-auto' : 'overflow-hidden',
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onClick={handleClick}
    >
      {/* min-h/min-w-full + m-auto centre the image while it fits and let
          it overflow from the top-left (fully scrollable) once zoomed. */}
      <div className="flex min-h-full w-max min-w-full">
        <div className="relative m-auto">
          {previewSrc && !fullLoaded && (
            <img
              src={previewSrc}
              alt={item.filename || 'Image'}
              className="max-h-[90vh] max-w-[90vw] object-contain"
            />
          )}
          <img
            ref={imgRef}
            src={item.url}
            alt={item.filename || 'Image'}
            draggable={false}
            className={cn(
              'transition-opacity select-none',
              zoomAt
                ? 'max-w-none cursor-zoom-out'
                : 'max-h-[90vh] max-w-[90vw] object-contain',
              !zoomAt && zoomable && 'cursor-zoom-in',
              fullLoaded ? 'opacity-100' : 'absolute inset-0 opacity-0',
            )}
            onLoad={() => {
              setFullLoaded(true);
              measure();
            }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Back-compat shim: existing attachment-mode callers used to get a smaller
 * default-profile preview for free via useAttachmentUrl. Keep that working
 * by detecting the /api/attachments/{checksum} URL shape and asking the
 * legacy hook for the preview variant.
 */
function useLegacyAttachmentPreview(item: LightboxItem): string | null {
  const match = /\/attachments\/([a-f0-9]{32})\b/i.exec(item.url);
  // Hooks must be called unconditionally; pass a dummy payload when not an
  // attachment URL so the hook still runs but its URL is ignored.
  const payload: AddAttachmentPayload = match
    ? {
        checksum: match[1],
        size: 0,
        contentType: item.contentType as AddAttachmentPayload['contentType'],
        filename: item.filename ?? '',
      }
    : {
        checksum: '0'.repeat(32),
        size: 0,
        contentType: 'image/png' as AddAttachmentPayload['contentType'],
        filename: '',
      };
  const [previewUrl] = useAttachmentUrl(payload);
  return match ? previewUrl : null;
}
